import { useState, useEffect, useCallback } from 'react'
import { readAdminToken as readToken, writeAdminToken as writeToken } from '../services/adminAuth.js'
import StaffChat from '../components/StaffChat.jsx'
import { useChatUnread, registerStaffDevice, unregisterStaffDevice } from '../services/staffChat.js'
import { useViewParams } from '../services/viewHistory.js'

const API = import.meta.env.VITE_API_URL || ''

const VIEWS = [
  { value: 'reported', label: 'Reported' },
  { value: 'prayer', label: 'Prayer' },
  { value: 'hidden', label: 'Hidden' },
  { value: 'recent', label: 'Recent' },
  { value: 'bans', label: 'Bans' },
  { value: 'pastors', label: 'Pastors' },
]

function formatDate(str) {
  return new Date(str).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
}



export default function AdminPage() {
  const [token, setToken] = useState(readToken)
  const [view, setView] = useState('reported')
  const [query, setQuery] = useState('')
  const [items, setItems] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  // Unteransichten in der URL: ?screen=chat|password, &c=<Unterhaltung>, &new=1
  const nav = useViewParams()
  const screen = nav.params.get('screen')
  const activeChat = parseInt(nav.params.get('c')) || null
  const leaveChat = useCallback(() => nav.back({ screen: null, c: null, new: null }), [nav])
  const { unread, refresh: refreshUnread } = useChatUnread(readToken, !!token)

  useEffect(() => { if (token) registerStaffDevice() }, [token])

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${API}/api/admin${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...options.headers,
      },
    })
    const data = await res.json().catch(() => ({}))
    if (res.status === 401) {
      writeToken('')
      setToken('')
    }
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`)
    return data
  }, [token])

  const load = useCallback(async () => {
    if (!token) return
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ view })
      if (query.trim()) params.set('q', query.trim())
      const [s, list] = await Promise.all([
        api('/stats'),
        view === 'bans' ? api('/bans') : view === 'pastors' ? api('/pastors') : api(`/confessions?${params}`),
      ])
      setStats(s)
      setItems(list)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [api, token, view, query])

  useEffect(() => { load() }, [view, token]) // eslint-disable-line react-hooks/exhaustive-deps

  // Aktion ausführen, dann Liste + Zahlen neu laden
  const act = useCallback(async (path, body, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return
    try {
      await api(path, { method: 'POST', body: JSON.stringify(body || {}) })
      await load()
    } catch (err) {
      setError(err.message)
    }
  }, [api, load])

  const ban = useCallback((kind, id) => {
    const reason = window.prompt('Ban reason (optional):', '')
    if (reason === null) return
    const purge = window.confirm('Also hide ALL confessions and delete ALL comments of this author?')
    act(`/${kind}/${id}/ban`, { reason, purge })
  }, [act])

  async function logout() {
    await unregisterStaffDevice(token)
    writeToken('')
    setToken('')
  }

  if (!token) return <AdminLogin error={error} onLogin={t => { writeToken(t); setError(null); setToken(t) }} />

  if (screen === 'chat') {
    return (
      <div className="admin admin-chat">
        <header className="admin-header">
          <button className="admin-back" onClick={leaveChat}>← Moderation</button>
          <h1>Chat</h1>
        </header>
        <StaffChat
          getToken={readToken}
          activeId={activeChat}
          showPicker={nav.params.get('new') === '1'}
          onOpen={id => (activeChat ? nav.replace({ c: id, new: null }) : nav.push({ c: id, new: null }))}
          onPicker={() => nav.push({ new: 1 })}
          onStart={id => nav.replace({ new: null, c: id })}
          onBack={() => nav.back({ c: null, new: null })}
          onActivity={refreshUnread}
        />
      </div>
    )
  }

  return (
    <div className="admin">
      <header className="admin-header">
        <a href="/" className="admin-back">← Reverend</a>
        <h1>Moderation</h1>
        <ChatButton unread={unread} onClick={() => nav.push({ screen: 'chat' })} />
        <button className="btn btn-ghost" onClick={logout}>
          Log out
        </button>
      </header>

      {stats && (
        <div className="admin-stats">
          <Stat label="Open reports" value={stats.reported} warn={stats.reported > 0} />
          <Stat label="Prayer requests" value={stats.prayer_open} warn={stats.prayer_open > 0} />
          <Stat label="Hidden" value={stats.hidden} />
          <Stat label="Visible" value={stats.visible} />
          <Stat label="Posts 24h" value={stats.confessions_24h} />
          <Stat label="Comments 24h" value={stats.comments_24h} />
          <Stat label="Bans" value={stats.bans} />
        </div>
      )}

      <div className="admin-toolbar">
        <div className="sort-tabs" role="tablist">
          {VIEWS.map(v => (
            <button
              key={v.value}
              role="tab"
              aria-selected={view === v.value}
              className={`sort-tab ${view === v.value ? 'active' : ''}`}
              onClick={() => setView(v.value)}
            >
              {v.label}
            </button>
          ))}
        </div>
        {view !== 'bans' && view !== 'pastors' && (
          <form className="admin-search" onSubmit={e => { e.preventDefault(); load() }}>
            <input
              className="comment-input"
              type="search"
              placeholder="Search text…"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
            <button className="btn btn-ghost" type="submit">Search</button>
          </form>
        )}
      </div>

      {error && <div className="feed-state feed-error" role="alert">{error}</div>}
      {loading && <div className="feed-state"><div className="spinner" aria-label="Loading…" /></div>}

      {view === 'pastors' && <NewPastorForm api={api} onCreated={load} />}

      {!loading && items.length === 0 && (
        <div className="feed-state feed-empty">
          {view === 'reported' ? 'No open reports. 🙏' : view === 'prayer' ? 'No prayer requests. 🙏' : 'Nothing here.'}
        </div>
      )}

      <div className="admin-list">
        {view === 'pastors'
          ? items.map(p => <PastorAccount key={p.id} pastor={p} api={api} act={act} />)
          : view === 'bans'
          ? items.map(b => (
            <div key={b.id} className="admin-item">
              <div className="admin-item-meta">
                <span>Ban #{b.id}</span>
                <span>{formatDate(b.created_at)}</span>
                <span>{b.confessions} posts · {b.comments} comments</span>
              </div>
              <p className="admin-item-text">{b.reason || <em>No reason given</em>}</p>
              <div className="admin-actions">
                <button className="action-btn" onClick={() => act(`/bans/${b.id}/delete`, null, 'Lift this ban?')}>
                  Unban
                </button>
              </div>
            </div>
          ))
          : items.map(c => (
            <AdminConfession key={c.id} confession={c} api={api} act={act} ban={ban} onReplied={load} />
          ))}
      </div>
    </div>
  )
}

function Stat({ label, value, warn }) {
  return (
    <div className={`admin-stat ${warn ? 'warn' : ''}`}>
      <span className="admin-stat-value">{value}</span>
      <span className="admin-stat-label">{label}</span>
    </div>
  )
}

function AdminConfession({ confession: c, api, act, ban, onReplied }) {
  const [comments, setComments] = useState(null)
  const [open, setOpen] = useState(false)
  const [reply, setReply] = useState('')
  const [replying, setReplying] = useState(false)
  const [replyError, setReplyError] = useState(null)

  // Antwort als Reverend (Admin-Kommentar) – markiert die Seelsorge-Anfrage als beantwortet
  async function sendReply(e) {
    e.preventDefault()
    const text = reply.trim()
    if (!text) return
    setReplying(true)
    setReplyError(null)
    try {
      const res = await fetch(`${API}/api/confessions/${c.id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${readToken()}` },
        body: JSON.stringify({ text }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Error posting reply.')
      setReply('')
      setComments(null)
      setOpen(false)
      await onReplied()
    } catch (err) {
      setReplyError(err.message)
    } finally {
      setReplying(false)
    }
  }

  const loadComments = useCallback(async () => {
    try {
      setComments(await api(`/confessions/${c.id}/comments`))
    } catch {
      setComments([])
    }
  }, [api, c.id])

  async function toggleComments() {
    if (!open && comments === null) await loadComments()
    setOpen(o => !o)
  }

  async function deleteComment(id) {
    if (!window.confirm('Delete this comment?')) return
    try {
      await api(`/comments/${id}/delete`, { method: 'POST', body: '{}' })
      setComments(prev => prev.filter(x => x.id !== id))
    } catch (err) {
      window.alert(err.message)
    }
  }

  return (
    <div className={`admin-item ${c.is_hidden ? 'is-hidden' : ''}`}>
      <div className="admin-item-meta">
        <span>#{c.id}</span>
        <span className="category-badge">{c.category}</span>
        <span>{formatDate(c.created_at)}</span>
        <span>Karma {c.karma_score}</span>
        {c.is_hidden && <span className="admin-tag">hidden</span>}
        {c.approved_at && <span className="admin-tag ok">approved</span>}
        {c.wants_pastor && (c.pastor_answered_at
          ? <span className="admin-tag ok">✝ answered{c.answered_by ? ` by ${c.answered_by}` : ''}</span>
          : <span className="admin-tag pastor">✝ pastor requested</span>)}
        {c.author_banned && <span className="admin-tag danger">author banned</span>}
        <span>Author: {c.author_posts} {c.author_posts === 1 ? 'post' : 'posts'}</span>
      </div>

      <p className="admin-item-text">{c.text}</p>

      {c.total_reports > 0 && (
        <div className="admin-reports">
          <strong>{c.open_reports} open</strong> / {c.total_reports} total reports:{' '}
          {c.reasons.map(r => `${r.reason} (${r.count})`).join(', ')}
        </div>
      )}

      <div className="admin-actions">
        {c.is_hidden || c.open_reports > 0 ? (
          <button className="action-btn approve" onClick={() => act(`/confessions/${c.id}/approve`)}>
            ✓ Approve
          </button>
        ) : null}
        {!c.is_hidden && (
          <button className="action-btn" onClick={() => act(`/confessions/${c.id}/hide`)}>
            Hide
          </button>
        )}
        <button
          className="action-btn danger"
          onClick={() => act(`/confessions/${c.id}/delete`, null, `Permanently delete confession #${c.id}?`)}
        >
          Delete
        </button>
        {!c.author_banned && (
          <button className="action-btn danger" onClick={() => ban('confessions', c.id)}>
            Ban author
          </button>
        )}
        {c.wants_pastor && (c.pastor_answered_at
          ? <button className="action-btn" onClick={() => act(`/confessions/${c.id}/pastor-reopen`)}>Reopen</button>
          : <button className="action-btn approve" onClick={() => act(`/confessions/${c.id}/pastor-done`)}>✓ Mark done</button>)}
        <button className="action-btn" onClick={toggleComments} aria-expanded={open}>
          💬 {c.comment_count}
        </button>
      </div>

      {open && comments && (
        <ul className="admin-comments">
          {comments.length === 0 && <li className="comment-empty">No comments.</li>}
          {comments.map(cm => (
            <li key={cm.id}>
              <span className="admin-comment-author">
                {cm.pastor_name ? `✝ ${cm.pastor_name}` : cm.is_admin ? '✝ Reverend' : (cm.author_tag || '—')}{cm.is_op && ' (OP)'}{cm.author_banned && ' 🚫'}
              </span>
              <span className="admin-comment-text">{cm.text}</span>
              <span className="admin-comment-actions">
                <button className="action-btn danger" onClick={() => deleteComment(cm.id)}>Delete</button>
                {!cm.author_banned && (
                  <button className="action-btn danger" onClick={() => ban('comments', cm.id)}>Ban</button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {c.wants_pastor && !c.is_hidden && (
        <form className="admin-reply" onSubmit={sendReply}>
          <textarea
            className="comment-input"
            rows={3}
            maxLength={200}
            placeholder="Reply as ✝ Reverend…"
            value={reply}
            onChange={e => setReply(e.target.value)}
            disabled={replying}
          />
          <button className="btn btn-primary" type="submit" disabled={replying || !reply.trim()}>
            {replying ? 'Sending…' : 'Reply'}
          </button>
          {replyError && <p className="comment-error">{replyError}</p>}
        </form>
      )}
    </div>
  )
}

function AdminLogin({ onLogin, error }) {
  const [value, setValue] = useState('')
  return (
    <div className="admin admin-login">
      <a href="/" className="admin-back">← Reverend</a>
      <h1>✝ Reverend Moderation</h1>
      <form onSubmit={e => { e.preventDefault(); if (value.trim()) onLogin(value.trim()) }}>
        <input
          className="comment-input"
          type="password"
          placeholder="Admin token"
          autoComplete="current-password"
          value={value}
          onChange={e => setValue(e.target.value)}
          autoFocus
        />
        <button className="btn btn-primary" type="submit" disabled={!value.trim()}>Log in</button>
      </form>
      {error && <p className="comment-error">{error}</p>}
    </div>
  )
}

// ── Seelsorger-Konten verwalten ──────────────────────────────────────────────
function NewPastorForm({ api, onCreated }) {
  const [form, setForm] = useState({ username: '', display_name: '', password: '' })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const set = key => e => setForm(f => ({ ...f, [key]: e.target.value }))

  async function submit(e) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await api('/pastors', { method: 'POST', body: JSON.stringify(form) })
      setForm({ username: '', display_name: '', password: '' })
      await onCreated()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="admin-item pastor-form" onSubmit={submit}>
      <strong>Add verified pastor</strong>
      <input className="comment-input" placeholder="Display name (shown publicly, e.g. Fr. Michael)" value={form.display_name} onChange={set('display_name')} maxLength={60} />
      <input className="comment-input" placeholder="Username (login)" value={form.username} onChange={set('username')} maxLength={40} autoCapitalize="none" autoComplete="off" />
      <input className="comment-input" type="password" placeholder="Initial password (min. 10 characters)" value={form.password} onChange={set('password')} autoComplete="new-password" />
      <button className="btn btn-primary" type="submit" disabled={busy || !form.username || !form.display_name || !form.password}>
        {busy ? 'Creating…' : 'Create account'}
      </button>
      {error && <p className="comment-error">{error}</p>}
    </form>
  )
}

function PastorAccount({ pastor: p, api, act }) {
  async function resetPassword() {
    const password = window.prompt(`New password for ${p.display_name} (min. 10 characters):`, '')
    if (!password) return
    try {
      await api(`/pastors/${p.id}/password`, { method: 'POST', body: JSON.stringify({ password }) })
      window.alert('Password changed. The pastor has been logged out on all devices.')
    } catch (err) {
      window.alert(err.message)
    }
  }

  return (
    <div className={`admin-item ${p.is_active ? '' : 'is-hidden'}`}>
      <div className="admin-item-meta">
        <span>✝ <strong>{p.display_name}</strong></span>
        <span>@{p.username}</span>
        {!p.is_active && <span className="admin-tag danger">deactivated</span>}
        <span>{p.replies} {p.replies === 1 ? 'reply' : 'replies'}</span>
        <span>Last login: {p.last_login_at ? formatDate(p.last_login_at) : 'never'}</span>
      </div>
      <div className="admin-actions">
        <button className="action-btn" onClick={resetPassword}>Reset password</button>
        {p.is_active
          ? <button className="action-btn danger" onClick={() => act(`/pastors/${p.id}/deactivate`, null, `Deactivate ${p.display_name}? They will be logged out immediately.`)}>Deactivate</button>
          : <button className="action-btn approve" onClick={() => act(`/pastors/${p.id}/activate`)}>Reactivate</button>}
      </div>
    </div>
  )
}

function ChatButton({ unread, onClick }) {
  return (
    <button className="btn btn-ghost chat-open-btn" onClick={onClick} aria-label={`Chat${unread ? `, ${unread} unread` : ''}`}>
      💬 Chat
      {unread > 0 && <span className="chat-unread">{unread > 99 ? '99+' : unread}</span>}
    </button>
  )
}
